import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@amber/ui";
import type { EvidenceRecord, ProblemView, TurnView } from "../../contracts/index";
import { recordQuery, translationQuery, useRequestTranslation } from "../queries";
import type { EvidenceSearch } from "../search";
import { useEditing } from "../editing/Editing";
import type { CopyAction } from "../copies/Copies";
import type { PlayerState } from "./Player";
import { PlayerSection } from "./PlayerSection";
import { RecordDialogs, useMoreCorrections, type RecordDialog } from "./RecordDialogs";
import { RecordHeader, type RecordRole, type RecordSearchHandler } from "./RecordHeader";
import { FailedPanel, PinnedBanner, RecordUnavailable, RestrictionBanner } from "./RecordNotices";
import { RecordTabs } from "./RecordTabs";
import { TranscriptPanel } from "./Transcript";
import { usePlayer } from "./usePlayer";

export interface EvidenceRecordProps {
  itemId: string;
  version: number | undefined;
  search: EvidenceSearch;
  role: RecordRole;
  onSearch: RecordSearchHandler;
}

function useAutoTranslation(itemId: string, version: number | undefined, search: EvidenceSearch, enabled: boolean) {
  const translationOn = search.lang === "en";
  const translation = useQuery({
    ...translationQuery(itemId, "en", version),
    enabled: translationOn && enabled,
  });
  const requestTranslation = useRequestTranslation(itemId, version);
  const requested = useRef(false);

  useEffect(() => {
    if (!translationOn || !enabled || requested.current) return;
    if (translation.data?.state === "waiting" && translation.data.message === "Not translated yet") {
      requested.current = true;
      requestTranslation.mutate("en");
    }
  }, [translationOn, enabled, translation.data, requestTranslation]);
  const status = () => {
    if (requestTranslation.isPending || translation.isFetching) return "Translating…";
    return translation.data && translation.data.state !== "ready" ? translation.data.message : null;
  };
  const tState = translationOn ? status() : null;
  return { translationOn, translation, tState };
}

function useTurnFromUrl(
  tSeconds: number | undefined,
  turns: readonly TurnView[],
  player: PlayerState,
  setSelectedTurn: (idx: number) => void,
) {
  const tSelected = useRef(false);
  useEffect(() => {
    if (tSelected.current || tSeconds === undefined || turns.length === 0) return;
    tSelected.current = true;
    const ms = tSeconds * 1000;
    const hit = [...turns].reverse().find((t) => t.start_ms <= ms) ?? turns[0];
    if (hit) setSelectedTurn(hit.idx);
    player.seek(ms);
  }, [tSeconds, turns, player]);
}

function useParagraphFromUrl(
  paragraph: number | undefined,
  turns: readonly TurnView[],
  setSelectedTurn: (idx: number) => void,
) {
  const pSelected = useRef(false);
  useEffect(() => {
    if (pSelected.current || paragraph === undefined || turns.length === 0) return;
    pSelected.current = true;
    const hit = turns.find(
      (t) => t.locator && t.locator.paragraphs[0] <= paragraph && paragraph <= t.locator.paragraphs[1],
    );
    if (hit) setSelectedTurn(hit.idx);
  }, [paragraph, turns]);
}

function usePlayerKeys(
  player: PlayerState,
  turns: readonly TurnView[],
  setSelectedTurn: (idx: number) => void,
  onTiming: (() => void) | null,
) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.closest("input, textarea, select, [contenteditable=true], [role=dialog], [role=menu]") ?? null)
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === " " || e.key === "k") {
        if (target?.closest("button, a")) return;
        e.preventDefault();
        player.toggle();
      } else if (e.key === "j") player.seek(player.timeMs - 10_000);
      else if (e.key === "l") player.seek(player.timeMs + 10_000);
      else if ((e.key === "t" || e.key === "T") && onTiming) onTiming();
      else if (e.key === "]" || e.key === "[") {
        const now = player.timeMs;
        const t =
          e.key === "]"
            ? turns.find((x) => x.start_ms > now + 50)
            : [...turns].reverse().find((x) => x.start_ms < now - 1500);
        if (t) {
          setSelectedTurn(t.idx);
          player.seek(t.start_ms);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [player, turns, onTiming]);
}

function useTimingMode(record: EvidenceRecord | undefined) {
  const [timing, setTiming] = useState(false);
  const enter = useCallback(() => {
    setTiming(true);
  }, []);
  const canEnter = Boolean(record?.actions.timing.enabled && !record.pinned && record.transcript && !timing);
  return { timing, setTiming, enterByKey: canEnter ? enter : null };
}

function NoTranscript({ record }: { record: EvidenceRecord }) {
  if (record.failure) return <FailedPanel record={record} />;
  return (
    <EmptyState title="No transcript yet" className="my-auto">
      {record.status.title}. The transcript appears here after the Transcribe stage.
    </EmptyState>
  );
}

export function EvidenceRecordView({ itemId, version, search, role, onSearch }: EvidenceRecordProps) {
  const query = useQuery(recordQuery(itemId, version, search.communication));
  const record = query.data;
  const [selectedTurn, setSelectedTurn] = useState<number | null>(null);
  const [dialog, setDialog] = useState<RecordDialog | null>(null);
  const [copyAction, setCopyAction] = useState<CopyAction | null>(null);
  const { timing, setTiming, enterByKey } = useTimingMode(record);
  const [resolving, setResolving] = useState<ProblemView | null>(null);
  const editing = useEditing(itemId);
  const more = useMoreCorrections(setSelectedTurn, setDialog);
  const player = usePlayer(
    search.t === undefined ? undefined : search.t * 1000,
    record?.media?.duration_ms ?? record?.item.duration_ms ?? 0,
  );
  const { translationOn, translation, tState } = useAutoTranslation(
    itemId,
    version,
    search,
    Boolean(record?.actions.translate.enabled),
  );

  const turns = useMemo(() => record?.turns ?? [], [record]);
  useTurnFromUrl(search.t, turns, player, setSelectedTurn);
  useParagraphFromUrl(search.p, turns, setSelectedTurn);
  usePlayerKeys(player, turns, setSelectedTurn, enterByKey);

  if (!record) return <RecordUnavailable isError={query.isError} error={query.error} />;
  const tab = search.tab ?? "speakers";
  const canEdit = role !== "viewer";
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-surface">
      <PinnedBanner
        record={record}
        onLatest={() => {
          onSearch(search, null);
        }}
      />
      <RestrictionBanner
        record={record}
        onRestore={() => {
          setDialog("restore");
        }}
      />
      <RecordHeader
        record={record}
        search={search}
        role={role}
        onSearch={onSearch}
        onSeek={(ms) => {
          player.seek(ms);
        }}
        selectedTurn={selectedTurn}
        actions={{
          onRestrict: () => {
            setDialog("restrict");
          },
          onRestore: () => {
            setDialog("restore");
          },
          onReport: () => {
            setDialog("report");
          },
          onCopy: setCopyAction,
          onDetails: () => {
            setDialog("details");
          },
          onSplit: () => {
            setDialog("split");
          },
          onMerge: () => {
            setDialog("merge");
          },
          onTiming: () => {
            setTiming(true);
          },
          onCheck: () => {
            setDialog("check");
          },
        }}
      />
      <div className="mx-4 mb-3 grid h-[calc(100vh-268px)] min-h-[440px] grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)] gap-3 max-compact:mx-2 max-compact:flex max-compact:h-auto max-compact:flex-col">
        <PlayerSection
          record={record}
          turns={turns}
          player={player}
          editing={editing}
          selectedTurn={selectedTurn}
          setSelectedTurn={setSelectedTurn}
          timing={timing}
          setTiming={setTiming}
        />
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-divider bg-bg max-compact:h-[560px] max-compact:flex-none">
          {record.transcript && turns.length > 0 ? (
            <>
              {record.failure ? <FailedPanel record={record} /> : null}
              <TranscriptPanel
                record={record}
                turns={turns}
                speakers={record.speakers}
                player={player}
                selected={selectedTurn}
                onSelect={setSelectedTurn}
                translation={translation.data?.state === "ready" ? translation.data.translation : null}
                translationState={tState}
                translationOn={translationOn}
                onTranslation={(on) => {
                  onSearch({ ...search, lang: on ? "en" : undefined }, version ?? null);
                }}
                canEdit={canEdit}
                editing={editing}
                onResolve={setResolving}
                onMoreCorrections={more.open}
              />
            </>
          ) : (
            <NoTranscript record={record} />
          )}
        </div>
      </div>
      <RecordTabs
        record={record}
        tab={tab}
        onTab={(t) => {
          onSearch({ ...search, tab: t }, version ?? null);
        }}
        onSeek={(ms) => {
          player.seek(ms);
        }}
        onOpenVersion={(v) => {
          onSearch({ ...search, tab: undefined }, v);
        }}
      />
      <RecordDialogs
        record={record}
        turns={turns}
        selectedTurn={selectedTurn}
        timeMs={player.timeMs}
        editing={editing}
        dialog={dialog}
        setDialog={setDialog}
        copyAction={copyAction}
        setCopyAction={setCopyAction}
        resolving={resolving}
        setResolving={setResolving}
        more={more}
      />
    </div>
  );
}
